import { Market } from '../../../../../features/markets/types/Market'
import type { InternalFeatureMapFeature, InternalGameScreenshot } from '../../../../api/screenshot-links'
import { InternalCategoryFeature } from '../../../../types/InternalCategory'
import { BulkAssignChoice, BulkAssignFeature, BulkAssignGame, BulkAssignMarket, BulkAssignState, GameChoiceMap, UnstoredAnswer } from '../../types/BulkAssign'

export const featureBulkAssignPath = (legacyId: number, marketIso?: string) =>
  `/internal/features/${legacyId}/bulk-assign${marketIso ? `?marketIso=${encodeURIComponent(marketIso)}` : ''}`

/** The feature's markets in the stored order, once each; ids missing from `GET markets` are left out. */
export const getFeatureMarkets = (feature: BulkAssignFeature | undefined, markets: Market[] | undefined): BulkAssignMarket[] => {
  if (!feature || !markets) return []
  const byId = new Map(markets.map((market) => [market.id, market]))
  const seen = new Set<string>()
  return (feature.markets ?? []).flatMap((id) => {
    const market = byId.get(id)
    if (!market || seen.has(market.iso)) return []
    seen.add(market.iso)
    return [{ id: market.id, iso: market.iso, name: market.name }]
  })
}

/** Legacy's market list: the added markets, or the selected one. */
export const getBulkAssignIsos = (addedIsos: string[], selectedIso: string | undefined) => (addedIsos.length ? addedIsos : selectedIso ? [selectedIso] : [])

export const addIso = (isos: string[], iso: string) => (isos.includes(iso) ? isos : [...isos, iso])

export const addAllIsos = (isos: string[], markets: BulkAssignMarket[]) => markets.reduce((all, market) => addIso(all, market.iso), isos)

export const toInitialState = (stored: GameChoiceMap | undefined): BulkAssignState => ({ answers: { ...(stored ?? {}) }, changed: {}, changedBulk: {} })

export const isGameChanged = (state: BulkAssignState, gameId: string) => !!(state.changed[gameId] || state.changedBulk[gameId])

const revert = (state: BulkAssignState, stored: GameChoiceMap, gameId: string): BulkAssignState => {
  const answers = { ...state.answers }
  if (stored[gameId] === undefined) delete answers[gameId]
  else answers[gameId] = stored[gameId]
  const changed = { ...state.changed }
  const changedBulk = { ...state.changedBulk }
  delete changed[gameId]
  delete changedBulk[gameId]
  return { answers, changed, changedBulk }
}

/**
 * A game's choice. Picking its stored answer, or the selected choice of a changed game, goes back to the stored answer and out of the
 * save; the selected choice of an unchanged game stays, since the backend can't clear an answer.
 */
export const selectGameChoice = (state: BulkAssignState, stored: GameChoiceMap, gameId: string, weightId: number): BulkAssignState => {
  if (state.answers[gameId] === weightId) return isGameChanged(state, gameId) ? revert(state, stored, gameId) : state
  if (stored[gameId] === weightId) return revert(state, stored, gameId)
  return { ...state, answers: { ...state.answers, [gameId]: weightId }, changed: { ...state.changed, [gameId]: true } }
}

/** Legacy's Apply: the games without an answer, or with `force` every listed game. */
export const applyBulkChoice = (state: BulkAssignState, games: BulkAssignGame[], weightId: number, force: boolean): BulkAssignState => {
  const answers = { ...state.answers }
  const changedBulk = { ...state.changedBulk }
  games.forEach((game) => {
    if (!force && answers[game.id]) return
    answers[game.id] = weightId
    changedBulk[game.id] = true
  })
  return { ...state, answers, changedBulk }
}

/** Legacy's save body: games changed in bulk, then games changed one by one, each with its current answer. */
export const toBulkAssignBody = (state: BulkAssignState): GameChoiceMap => {
  const body: GameChoiceMap = {}
  Object.keys(state.changedBulk).forEach((gameId) => {
    if (state.answers[gameId]) body[gameId] = state.answers[gameId]
  })
  Object.keys(state.changed).forEach((gameId) => {
    if (state.answers[gameId]) body[gameId] = state.answers[gameId]
  })
  return body
}

export const countWithoutChoice = (games: BulkAssignGame[] | undefined, answers: GameChoiceMap) => (games ?? []).filter((game) => !answers[game.id]).length

export const getGameIdsWithoutChoice = (games: BulkAssignGame[] | undefined, answers: GameChoiceMap) =>
  new Set((games ?? []).filter((game) => !answers[game.id]).map((game) => game.id))

/** The listed games: the "without a choice" snapshot when set, and the name search (case-insensitive, anywhere in the name). */
export const filterBulkAssignGames = (games: BulkAssignGame[] | undefined, search: string, onlyIds?: Set<string>) => {
  const needle = search.trim().toLowerCase()
  return (games ?? []).filter((game) => (!onlyIds || onlyIds.has(game.id)) && (!needle || (game.name ?? '').toLowerCase().includes(needle)))
}

export const getChoiceLabel = (choices: BulkAssignChoice[] | null | undefined, weightId: number | undefined) =>
  weightId === undefined ? undefined : choices?.find((choice) => choice.weightId === weightId)?.choice

/** Legacy shows `---` for a missing or zero GPS and rounds the rest. */
export const formatGps = (game: BulkAssignGame, iso: string | undefined) => {
  const gps = iso ? game.gpsPerMarket?.[iso] : undefined
  return gps ? String(Math.round(gps)) : '---'
}

/** Games whose sent answer isn't stored in every market of the save, with those markets. */
export const getUnstoredAnswers = (body: GameChoiceMap, storedByIso: Record<string, GameChoiceMap>): UnstoredAnswer[] =>
  Object.entries(body).flatMap(([gameId, weightId]) => {
    const isos = Object.entries(storedByIso)
      .filter(([, stored]) => stored[gameId] !== weightId)
      .map(([iso]) => iso)
    return isos.length ? [{ gameId, isos }] : []
  })

export const maxBenchmarkFeatures = 2

export const toBenchmarkFeature = (feature: InternalCategoryFeature | InternalFeatureMapFeature): BulkAssignFeature => ({
  id: feature.id,
  legacyId: feature.legacyId,
  name: feature.name,
  categoryId: feature.categoryId,
  markets: feature.markets,
  choices: feature.choices?.map(({ weightId, choice }) => ({ weightId, choice })),
})

/** Removes a picked feature or adds one while there's room; legacy replaced the second pick instead. */
export const toggleBenchmarkFeature = (picked: BulkAssignFeature[], feature: BulkAssignFeature) => {
  if (picked.some((candidate) => candidate.legacyId === feature.legacyId)) return picked.filter((candidate) => candidate.legacyId !== feature.legacyId)
  return picked.length >= maxBenchmarkFeatures ? picked : [...picked, feature]
}

/** Legacy's filter: the game's screenshots tagged with the feature, whatever the choice. */
export const getFeatureScreenshots = (screenshots: InternalGameScreenshot[] | undefined, legacyId: number) =>
  (screenshots ?? []).filter((screenshot) => !!screenshot.features?.[legacyId])
